 
/**
 * Job Safety Audit Submission Component
 * ======================================
 *
 * Purpose:
 * Centralizes all reactive form logic for the Job Safety Audit (JSA) submission workflow.
 * This specific JSA implementation has been refactored into a single logical step while
 * still leveraging the generic step / section metadata provided by the form model.
 *
 * Distinguishing Characteristics (vs. other form components):
 * - Single-step runtime behavior: underlying model still defines multiple conceptual sections
 *   but navigation UI is suppressed when only one step remains.
 * - Exposure-driven normalization: when `fallExposure` == 'No' all checkbox answers are force-normalized
 *   to the string 'NA' and all related "Other Specify" text inputs are cleared regardless of previous values.
 * - Dynamic checkbox aggregation: checkbox field names are collected at runtime from metadata to avoid
 *   hard-coded duplication when flattening submission payloads.
 * - Dual-phase transformation: raw reactive form state (booleans) is converted to a flattened DTO with
 *   semantic string values ('Yes' | 'No' | 'NA') prior to model instantiation.
 * - Conditional validator orchestration: checkbox groups require at least one selection only when
 *   fall exposure is 'Yes'; otherwise validators are removed and groups are reset.
 * - Contextual "Other" field validators: corresponding text inputs toggle `required` based on the
 *   state of their related "Other" checkbox.
 *
 * Responsibilities Split:
 * - MODEL (JobSafetyAudit): Declarative description of fields, mapping labels, and step/section metadata.
 * - COMPONENT (this class): Runtime reactive form construction, conditional validation, value
 *   normalization / coercion, and attachment merging before submission.
 *
 * Data Integrity Guarantees:
 * - Idempotent flattening: calling `getFormData()` multiple times without intervening form changes
 *   returns equivalent objects.
 * - Normalization invariants: if `fallExposure` is 'No', no checkbox-derived field will emit 'Yes'/'No'.
 * - Validator consistency: removal / addition of validators does not emit extraneous value changes.
 *
 * Extension Points:
 * - Additional sections with checkbox groups can be added in the model without modifying transformation logic
 *   as long as they follow the same naming conventions captured by metadata.
 *
 * @author MacSafety Development Team
 * @version 1.1
 */

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep, Section, CheckboxGroup} from '../../../../../models/forms/form-steps.interface';
import { JobSafetyAudit } from '../../../../../models/forms/job-safety-audit/job-safety-audit.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';
import { simpleCheckboxValidator } from '../../../../../utils/form-validators';

@Component({
  selector: 'app-job-safety-audit',
  templateUrl: './job-safety-audit.component.html',
  styleUrls: ['./job-safety-audit.component.css'],
})
export class JobSafetyAuditComponent extends BaseFormComponent<JobSafetyAudit> implements OnInit {

  override formType = JobSafetyAudit.getFormInfo().formType;
  override model = new JobSafetyAudit();

  override steps: FormStep[] = JobSafetyAudit.getFormSubmissionSteps();

  // Section and checkbox group references (resolved in ngOnInit via resolveSections())
  basicsSection?: Section;
  fallHazardsSection?: Section;
  fallHazardsCheckboxGroup?: CheckboxGroup;
  fallProtectionSection?: Section;
  fallProtectionCheckboxGroup?: CheckboxGroup;
  proceduresSection?: Section;
  proceduresCheckboxGroup?: CheckboxGroup;
  overheadProtectionSection?: Section;
  overheadProtectionCheckboxGroup?: CheckboxGroup;
  rescuePlanSection?: Section;
  rescuePlanCheckboxGroup?: CheckboxGroup;
  workingUnderPlanSection?: Section;


  technicianName: string = '';

  // Checkbox arrays for each section
  fallHazardsCheckboxFields: { name: string; label: string }[] = [];
  fallProtectionCheckboxFields: { name: string; label: string }[] = [];
  proceduresCheckboxFields: { name: string; label: string }[] = [];
  overheadProtectionCheckboxFields: { name: string; label: string }[] = [];
  rescuePlanCheckboxFields: { name: string; label: string }[] = [];
  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService
  ) {
    super(fb, formService, router, entraService, attachmentService, toastService, signatureService, screenSizeService);

    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
    this.technicianName = this.entraService.userName();

  }

  /**
   * Angular lifecycle hook: initializes section references then defers to the base class
   * for generic initialization (which will call `initializeForm` and configure listeners).
   *
   * Order Matters: `resolveSections()` must run before `super.ngOnInit()` so that metadata-driven
   * checkbox arrays are available when the form groups are constructed.
   */
  override ngOnInit(): void {
    this.resolveSections();
    super.ngOnInit();
  }

  /**
   * Initializes the form with its controls and validators.
   * Sets up the form group with all required fields and their validation rules.
   */
  /**
   * Builds the root reactive form structure, including nested groups for logical sections
   * and dynamically generated checkbox controls. Applies initial validators and wires
   * conditional validator/normalization subscriptions.
   *
   * Side Effects:
   * - Populates local checkbox metadata arrays used both by the template and transformation logic.
   * - Registers value change subscriptions for conditional behaviors (via helper methods).
   */
  protected override initializeForm(): void {
    // Initialize checkbox arrays
    this.fallHazardsCheckboxFields = this.getFallHazardsCheckboxes();
    this.fallProtectionCheckboxFields = this.getFallProtectionCheckboxes();
    this.proceduresCheckboxFields = this.getProceduresCheckboxes();
    this.overheadProtectionCheckboxFields = this.getOverheadProtectionCheckboxes();
    this.rescuePlanCheckboxFields = this.getRescuePlanCheckboxes();

    // Create form groups for checkboxes with validators
    const fallHazardsGroup = this.fb.group(
      {},
      {
        validators: [simpleCheckboxValidator(1)], // Require at least 1 fall hazard
      }
    );
    this.fallHazardsCheckboxFields.forEach((checkbox) => {
      fallHazardsGroup.addControl(checkbox.name, this.fb.control(false));
    });

    const fallProtectionGroup = this.fb.group(
      {},
      {
        validators: [simpleCheckboxValidator(1)], // Require at least 1 fall protection method
      }
    );
    this.fallProtectionCheckboxFields.forEach((checkbox) => {
      fallProtectionGroup.addControl(checkbox.name, this.fb.control(false));
    });

    const proceduresGroup = this.fb.group(
      {},
      {
        validators: [simpleCheckboxValidator(1)], // Require at least 1 procedure
      }
    );
    this.proceduresCheckboxFields.forEach((checkbox) => {
      proceduresGroup.addControl(checkbox.name, this.fb.control(false));
    });

    const overheadProtectionGroup = this.fb.group(
      {},
      {
        validators: [simpleCheckboxValidator(1)], // Require at least 1 overhead protection method
      }
    );
    this.overheadProtectionCheckboxFields.forEach((checkbox) => {
      overheadProtectionGroup.addControl(checkbox.name, this.fb.control(false));
    });

    const rescuePlanGroup = this.fb.group(
      {},
      {
        validators: [simpleCheckboxValidator(1)], // Require at least 1 rescue plan item
      }
    );
    this.rescuePlanCheckboxFields.forEach((checkbox) => {
      rescuePlanGroup.addControl(checkbox.name, this.fb.control(false));
    });

    this.form = this.fb.group({
      basicInfo: this.fb.group({
        date: [this.model.date, Validators.required],
        callNumber: [this.model.callNumber, Validators.required],
        location: [this.model.location, Validators.required],
     
      }),
      fallHazards: this.fb.group({
        fallExposure: [this.model.fallExposure, Validators.required],
        hazards: fallHazardsGroup,
        otherFallHazardsSpecify: [this.model.otherFallHazardsSpecify],
      }),
      fallProtection: this.fb.group({
        methods: fallProtectionGroup,
        otherFallProtectionSpecify: [this.model.otherFallProtectionSpecify],
      }),
      procedures: this.fb.group({
        items: proceduresGroup,
        otherProceduresSpecify: [this.model.otherProceduresSpecify],
      }),
      overheadProtection: this.fb.group({
        methods: overheadProtectionGroup,
        otherOverheadProtectionSpecify: [this.model.otherOverheadProtectionSpecify],
      }),
      rescuePlan: this.fb.group({
        items: rescuePlanGroup,
        otherRescuePlanSpecify: [this.model.otherRescuePlanSpecify],
      }),
      workingUnderPlan: this.fb.group({
        techniciansWorkingUnderPlan: [this.model.techniciansWorkingUnderPlan, Validators.required],
        additionalComments: [this.model.additionalComments],
        technicianName: [this.technicianName, Validators.required],
      })
    });

    // Set up conditional validators for "other" fields
    this.setupConditionalValidators();
    // Set up fallExposure conditional logic
    this.setupFallExposureValidation();
  }

  /**
   * Resolves references to sections and checkbox groups from the form steps.
   * This is separated from property declarations so that if steps are dynamically
   * generated or altered prior to ngOnInit, we get the correct references.
   */
  /**
   * Resolves strongly-typed references to section and checkbox group metadata from the first step.
   *
   * Rationale:
   * Delays resolution until `ngOnInit` so that any upstream dynamic alterations to steps are honored.
   *
   * Safety:
   * - Gracefully no-ops if steps are undefined or empty.
   */
  private resolveSections(): void {
    const firstStep = this.steps?.[0];
    if (!firstStep) return;
    const sections = firstStep.sections || [];
    this.basicsSection = sections[0];
    this.fallHazardsSection = sections[1];
    this.fallProtectionSection = sections[2];
    this.proceduresSection = sections[3];
    this.overheadProtectionSection = sections[4];
    this.rescuePlanSection = sections[5];
    this.workingUnderPlanSection = sections[6];

    // Checkbox groups (each section in this form uses a single checkbox groups)
    this.fallHazardsCheckboxGroup = this.fallHazardsSection?.checkboxGroups?.[0];
    this.fallProtectionCheckboxGroup = this.fallProtectionSection?.checkboxGroups?.[0];
  this.proceduresCheckboxGroup = this.proceduresSection?.checkboxGroups?.[0];
  this.overheadProtectionCheckboxGroup = this.overheadProtectionSection?.checkboxGroups?.[0];
  this.rescuePlanCheckboxGroup = this.rescuePlanSection?.checkboxGroups?.[0];
  }

  // Form group getters
  /**
   * Accessor: returns the Basic Info form group.
   * @returns FormGroup containing date, callNumber, location controls.
   */
  get basicInfoFormGroup(): FormGroup {
    return this.form.get('basicInfo') as FormGroup;
  }

  /**
   * Accessor: returns the Fall Hazards parent group.
   */
  get fallHazardsFormGroup(): FormGroup {
    return this.form.get('fallHazards') as FormGroup;
  }

  /**
   * Accessor: returns the nested checkbox group for Fall Hazards selections.
   */
  get fallHazardsCheckboxesFormGroup(): FormGroup {
    return this.fallHazardsFormGroup.get('hazards') as FormGroup;
  }

  /**
   * Accessor: returns the Fall Protection parent group.
   */
  get fallProtectionFormGroup(): FormGroup {
    return this.form.get('fallProtection') as FormGroup;
  }

  /**
   * Accessor: returns checkbox group for Fall Protection methods.
   */
  get fallProtectionCheckboxesFormGroup(): FormGroup {
    return this.fallProtectionFormGroup.get('methods') as FormGroup;
  }

  /**
   * Accessor: returns Procedures parent group.
   */
  get proceduresFormGroup(): FormGroup {
    return this.form.get('procedures') as FormGroup;
  }

  /**
   * Accessor: returns Procedures checkbox selection group.
   */
  get proceduresCheckboxesFormGroup(): FormGroup {
    return this.proceduresFormGroup.get('items') as FormGroup;
  }

  /**
   * Accessor: returns Overhead Protection parent group.
   */
  get overheadProtectionFormGroup(): FormGroup {
    return this.form.get('overheadProtection') as FormGroup;
  }

  /**
   * Accessor: returns Overhead Protection checkbox selection group.
   */
  get overheadProtectionCheckboxesFormGroup(): FormGroup {
    return this.overheadProtectionFormGroup.get('methods') as FormGroup;
  }

  /**
   * Accessor: returns Rescue Plan parent group.
   */
  get rescuePlanFormGroup(): FormGroup {
    return this.form.get('rescuePlan') as FormGroup;
  }

  /**
   * Accessor: returns Rescue Plan checkbox selection group.
   */
  get rescuePlanCheckboxesFormGroup(): FormGroup {
    return this.rescuePlanFormGroup.get('items') as FormGroup;
  }

  /**
   * Accessor: returns Working Under Plan group containing technician-related fields.
   */
  get workingUnderPlanFormGroup(): FormGroup {
    return this.form.get('workingUnderPlan') as FormGroup;
  }

  /**
   * Convenience method used by the template to determine if fall exposure is 'Yes'.
   * This centralizes the logic instead of duplicating the form control access in the template.
   */
  /**
   * Helper determining if current fall exposure selection is explicitly 'No'.
   * Used for conditional template rendering and validation logic branching.
   */
  noFallExposure(): boolean {
    return this.fallHazardsFormGroup.get('fallExposure')?.value === 'No';
  }

  /**
   * Convenience method used by the template to determine if fall exposure field is blank
   * This centralizes the logic instead of duplicating the form control access in the template.
   */
  /**
   * Helper determining if fall exposure selection is unset / blank.
   * Treats '', null, or undefined as blank for consistent template checks.
   */
  isFallExposureFieldBlank(): boolean {
    const fallHazardsValue = this.fallHazardsFormGroup.get('fallExposure')?.value;
   return fallHazardsValue === '' || fallHazardsValue === null || fallHazardsValue === undefined;
  }

  /**
   * Retrieves the current form data and creates a new JobSafetyAudit instance.
   * This method is called when submitting the form.
   */
  /**
   * Transforms the nested reactive form value into a flattened `JobSafetyAudit` domain model.
   *
   * Processing Steps:
   * 1. Shallow-flattens each nested section into a single object.
   * 2. Aggregates all checkbox field names dynamically (metadata-driven).
   * 3. Applies exposure-based normalization:
   *    - If fallExposure == 'No': all checkbox-derived fields => 'NA'; specify fields cleared.
   *    - If fallExposure == 'Yes': booleans => 'Yes'/'No'; missing/empty => 'No'.
   * 4. Instantiates a new model instance and merges attachment references.
   *
   * Idempotency: Repeated calls without intervening form mutations produce equivalent objects.
   *
   * @returns Fully normalized `JobSafetyAudit` instance ready for submission.
   */
  protected override getFormData(): JobSafetyAudit {
    const formValue = this.form.value;
    
    const flattenedData = {
      ...formValue.basicInfo,
      fallExposure: formValue.fallHazards.fallExposure,
      ...formValue.fallHazards.hazards,
      otherFallHazardsSpecify: formValue.fallHazards.otherFallHazardsSpecify,
      ...formValue.fallProtection.methods,
      otherFallProtectionSpecify: formValue.fallProtection.otherFallProtectionSpecify,
      ...formValue.procedures.items,
      otherProceduresSpecify: formValue.procedures.otherProceduresSpecify,
      ...formValue.overheadProtection.methods,
      otherOverheadProtectionSpecify: formValue.overheadProtection.otherOverheadProtectionSpecify,
      ...formValue.rescuePlan.items,
      otherRescuePlanSpecify: formValue.rescuePlan.otherRescuePlanSpecify,
      ...formValue.workingUnderPlan
    };

    console.log('Flattened Form Data:', flattenedData);

    const exposure = flattenedData.fallExposure;

    // Dynamically derive checkbox field names from the prepared checkbox field arrays
    const checkboxFields = this.getAllCheckboxFieldNames();

    if (exposure === 'No') {
      // Override all checkbox answers to 'NA' and clear specify fields
      checkboxFields.forEach(f => { if (f in flattenedData) flattenedData[f] = 'NA'; });
      flattenedData.otherFallHazardsSpecify = '';
      flattenedData.otherFallProtectionSpecify = '';
      flattenedData.otherProceduresSpecify = '';
      flattenedData.otherOverheadProtectionSpecify = '';
      flattenedData.otherRescuePlanSpecify = '';
    } else if (exposure === 'Yes') {
      // Normalize boolean -> 'Yes'/'No' if still boolean (in case form controls remain boolean)
      checkboxFields.forEach(f => {
        if (typeof flattenedData[f] === 'boolean') {
          flattenedData[f] = flattenedData[f] ? 'Yes' : 'No';
        } else if (flattenedData[f] === '' || flattenedData[f] == null) {
          // Default unchecked to 'No'
          flattenedData[f] = 'No';
        }
      });
    }
    
    const jobSafetyAudit = new JobSafetyAudit(flattenedData);
    const updatedJobSafetyAudit = this.updateModelWithAttachments(jobSafetyAudit);
    return updatedJobSafetyAudit;
  }

  /**
   * Collects all checkbox field names from the section-specific checkbox field arrays.
   * This avoids hard-coding lists in multiple places and stays in sync with model metadata.
   */
  /**
   * Collects unique checkbox control field names across all configured sections.
   *
   * Rationale: Prevents drift between transformation logic and metadata; adding a new checkbox
   * section in the model automatically participates in normalization logic if its metadata is
   * exposed through the corresponding local arrays.
   *
   * @returns Array of distinct checkbox control names (order not guaranteed).
   */
  private getAllCheckboxFieldNames(): string[] {
    const aggregate = [
      ...this.fallHazardsCheckboxFields,
      ...this.fallProtectionCheckboxFields,
      ...this.proceduresCheckboxFields,
      ...this.overheadProtectionCheckboxFields,
      ...this.rescuePlanCheckboxFields
    ].map(f => f.name);
    // Use a Set to guard against accidental duplicates if a field name appears in multiple arrays
    return Array.from(new Set(aggregate));
  }

  /**
   * Sets up validation rules for each step of the job safety audit form.
   * Each step has specific validation requirements that must be met before proceeding.
   */
  /**
   * Applies default step-level validation semantics. For this single-step form the entire
   * form group is treated as one logical step. Skips configuration if global form requirements
   * are suppressed (e.g., administrative overrides or draft mode).
   */
  protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }

    // Always validate the single step since this is now a single-step form
    this.setupStepValidation('basic', this.form);
  }

  /**
   * Subscribes to fallExposure changes to dynamically adjust validator presence and
   * sanitize dependent values.
   *
   * Behavior:
   * - Adds minimum-selection validators to all checkbox groups only when exposure is 'Yes'.
   * - Clears validators and resets all checkbox / specify fields when exposure switches to 'No'.
   * - Suppresses emission of cascading value change events during validator reconfiguration.
   */
  private setupFallExposureValidation(): void {
    this.fallHazardsFormGroup.get('fallExposure')?.valueChanges.subscribe(exposure => {
      console.log('Fall Exposure changed to:', exposure);
      const isExposed = exposure === 'Yes';

      const checkboxGroups = [
        this.fallHazardsCheckboxesFormGroup,
        this.fallProtectionCheckboxesFormGroup,
        this.proceduresCheckboxesFormGroup,
        this.overheadProtectionCheckboxesFormGroup,
        this.rescuePlanCheckboxesFormGroup
      ];

      // Adjust validators based on exposure state
      checkboxGroups.forEach(group => {
        if (isExposed) {
          group.setValidators([simpleCheckboxValidator(1)]);
        } else {
          group.clearValidators();
        }
        group.updateValueAndValidity({ emitEvent: false });
      });

      // When not exposed, reset all related checkboxes and clear associated 'specify' text fields
      if (!isExposed) {
        checkboxGroups.forEach(group => group.reset());

        const specifyFieldPaths: string[] = [
          'fallHazards.otherFallHazardsSpecify',
          'fallProtection.otherFallProtectionSpecify',
          'procedures.otherProceduresSpecify',
          'overheadProtection.otherOverheadProtectionSpecify',
          'rescuePlan.otherRescuePlanSpecify'
        ];

        specifyFieldPaths.forEach(path => {
          const control = this.form.get(path);
          control?.reset('');
        });
      }
    });
  }



  /**
   * Wires reactive conditional `required` validators for each "Other Specify" text input
   * based on the state of its paired "Other" checkbox.
   *
   * Implementation Detail: Uses independent subscriptions for clarity and separation; could
   * be consolidated if performance / allocation becomes a concern, though current scale is minimal.
   */
  private setupConditionalValidators(): void {

 

    // Fall Hazards Other
    this.fallHazardsCheckboxesFormGroup.get('fallHazardsOther')?.valueChanges.subscribe(checked => {
      const control = this.fallHazardsFormGroup.get('otherFallHazardsSpecify');
      if (checked) {
        control?.setValidators([Validators.required]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    });

    // Fall Protection Other
    this.fallProtectionCheckboxesFormGroup.get('fallProtectionOther')?.valueChanges.subscribe(checked => {
      const control = this.fallProtectionFormGroup.get('otherFallProtectionSpecify');
      if (checked) {
        control?.setValidators([Validators.required]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    });

    // Procedures Other
    this.proceduresCheckboxesFormGroup.get('proceduresOther')?.valueChanges.subscribe(checked => {
      const control = this.proceduresFormGroup.get('otherProceduresSpecify');
      if (checked) {
        control?.setValidators([Validators.required]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    });

    // Overhead Protection Other
    this.overheadProtectionCheckboxesFormGroup.get('overheadProtectionOther')?.valueChanges.subscribe(checked => {
      const control = this.overheadProtectionFormGroup.get('otherOverheadProtectionSpecify');
      if (checked) {
        control?.setValidators([Validators.required]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    });

    // Rescue Plan Other
    this.rescuePlanCheckboxesFormGroup.get('rescuePlanOther')?.valueChanges.subscribe(checked => {
      const control = this.rescuePlanFormGroup.get('otherRescuePlanSpecify');
      if (checked) {
        control?.setValidators([Validators.required]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    });
    }
  

  // Checkbox data getters
  /**
   * Maps fall hazards checkbox metadata into template-consumable structures.
   * @returns Array of objects { name, label } or empty array if metadata absent.
   */
  getFallHazardsCheckboxes(): { name: string; label: string }[] {
    if (!this.fallHazardsCheckboxGroup?.fields?.length) return [];
    return this.fallHazardsCheckboxGroup.fields.map(raw => {
      const name = typeof raw === 'string' ? raw : (raw as any).name;
      return { name, label: this.fieldMappings[name] };
    });
  }

  /**
   * Maps fall protection checkbox metadata into template-consumable structures.
   */
  getFallProtectionCheckboxes(): { name: string; label: string }[] {
    if (!this.fallProtectionCheckboxGroup?.fields?.length) return [];
    return this.fallProtectionCheckboxGroup.fields.map(raw => {
      const name = typeof raw === 'string' ? raw : (raw as any).name;
      return { name, label: this.fieldMappings[name] };
    });
  }

  /**
   * Maps procedures checkbox metadata into template-consumable structures.
   */
  getProceduresCheckboxes(): { name: string; label: string }[] {
    if (!this.proceduresCheckboxGroup?.fields?.length) return [];
    return this.proceduresCheckboxGroup.fields.map(raw => {
      const name = typeof raw === 'string' ? raw : (raw as any).name;
      return { name, label: this.fieldMappings[name] };
    });
  } 
   

  /**
   * Maps overhead protection checkbox metadata into template-consumable structures.
   */
  getOverheadProtectionCheckboxes(): { name: string; label: string }[] {
    if (!this.overheadProtectionCheckboxGroup?.fields?.length) return [];
    return this.overheadProtectionCheckboxGroup.fields.map(raw => {
      const name = typeof raw === 'string' ? raw : (raw as any).name;
      return { name, label: this.fieldMappings[name] };
    });
  }

   

  /**
   * Maps rescue plan checkbox metadata into template-consumable structures.
   */
  getRescuePlanCheckboxes(): { name: string; label: string }[] {
    if (!this.rescuePlanCheckboxGroup?.fields?.length) return [];
    return this.rescuePlanCheckboxGroup.fields.map(raw => {
      const name = typeof raw === 'string' ? raw : (raw as any).name;
      return { name, label: this.fieldMappings[name] };
    });
  }


}
